import { GlobeHemisphereWest } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import clsx from "clsx";

import { ProxyLocationTag } from "common/components/Proxy";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import useProxies from "common/datahooks/useProxies";
import useWorkspaceProxies from "common/datahooks/useWorkspaceProxies";
import type { WorkspaceAccount } from "common/types";

import useAccountProxy from "../../datahooks/useAccountProxy";

export default function AccountProxyTag({
  size,
  account,
}: {
  size: "sm" | "md";
  account: WorkspaceAccount;
}) {
  const { accountProxy, isLoadingAccountProxy } = useAccountProxy(account);
  const { proxies, isLoadingProxies } = useProxies();
  const { workspaceProxies, isLoadingWorkspaceProxies } = useWorkspaceProxies();
  const { type: accountType } = account;

  if (accountType === "rental") {
    return (
      <Tag size={size} variant="tertiary" leftIcon={<GlobeHemisphereWest />}>
        Automatic
      </Tag>
    );
  }

  if (isLoadingAccountProxy || isLoadingWorkspaceProxies || isLoadingProxies) {
    return (
      <Skeleton
        className={clsx("inline-flex", size === "sm" ? "h-5 w-16" : "h-6 w-20")}
      />
    );
  }

  if (!accountProxy) {
    return (
      <Tag size={size} variant="tertiary">
        No proxy
      </Tag>
    );
  }

  let isValid = true;
  let location: { countryCode: string; name: string };
  if (accountProxy.length === 2) {
    const proxy = proxies.find(
      ({ countryCode }) => countryCode === accountProxy,
    );
    // if the selected proxy has been removed from our proxy list it's no longer valid
    if (proxy) {
      location = { countryCode: proxy.countryCode, name: proxy.country };
    } else {
      isValid = false;
    }
  } else {
    const proxy = workspaceProxies.find(({ id }) => id === accountProxy);

    if (proxy) {
      isValid = proxy.valid;

      if (isValid) {
        location = {
          countryCode: proxy.location.countryISO,
          name: proxy.location.city || proxy.location.country,
        };
      }
    } else {
      Sentry.captureMessage("Custom Proxy not found");
      isValid = false;
    }
  }

  return <ProxyLocationTag isValid={isValid} location={location} size={size} />;
}
